import { Component, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UsersService } from '../../../../core/services/user/users.service';
import {
  UserListModel,
  UserStatus,
} from '../../../../core/models/user/user-list.model';
import { finalize } from 'rxjs';
import { RouterModule } from '@angular/router';
import { ModalComponent } from '../../../shared/modal/modal.component';
import { ToastComponent } from '../../../shared/toast/toast.component';
import { RolesService } from '../../../../core/services/user/roles.service';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    ModalComponent,
    ToastComponent,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  providers: [DatePipe],
})
export class UserListComponent {
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  users: UserListModel[] = [];
  totalUser: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  userListLoading: boolean = true;
  showToast: boolean = false;
  toastMessage: string = '';
  toastType: 'success' | 'error' = 'success';
  deleteModalId: string = '';
  userStatus: UserStatus[] = [
    {
      id: '2',
      title: 'Active',
    },
    {
      id: '3',
      title: 'DeActive',
    },
    {
      id: '4',
      title: 'Blocked',
    },
  ];
  filterForm = {
    nameSurname: '',
    role: null,
    email: '',
    pid: '',
    status: null,
    startCreatedDate: null as string | null,
    endCreatedDate: null as string | null,
    startLastLoginDate: null as string | null,
    endLastLoginDate: null as string | null,
    organization: null,
  };
  roles: any = [];
  organizationList: any = [];
  maxDate: string;

  constructor(
    private userService: UsersService,
    private rolesService: RolesService,
    private customersService: CustomersService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    const userLang = this.languageService.getLanguage() || 'en';
    this.translate.use(userLang);
  }

  ngOnInit(): void {
    this.getUserList(this.currentPage, this.pageSize);
    this.getRoles();
    this.getOrganizations();
  }

  getRoles(): void {
    this.rolesService.getRoles().subscribe({
      next: (res) => {
        this.roles = res.items.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        );
      },
      error: () => {},
    });
  }

  filterUserList(e: Event) {
    e.preventDefault();

    if (this.filterForm.startCreatedDate) {
      const transformedDate = new Date(this.filterForm.startCreatedDate);
      if (!isNaN(transformedDate.getTime())) {
        this.filterForm.startCreatedDate = this.datePipe.transform(
          transformedDate,
          'dd-MM-yyyy'
        );
      }
    }

    if (this.filterForm.endCreatedDate) {
      const transformedDate = new Date(this.filterForm.endCreatedDate);
      if (!isNaN(transformedDate.getTime())) {
        this.filterForm.endCreatedDate = this.datePipe.transform(
          transformedDate,
          'dd-MM-yyyy'
        );
      }
    }

    if (this.filterForm.startLastLoginDate) {
      const transformedDate = new Date(this.filterForm.startLastLoginDate);
      if (!isNaN(transformedDate.getTime())) {
        this.filterForm.startLastLoginDate = this.datePipe.transform(
          transformedDate,
          'dd-MM-yyyy'
        );
      }
    }

    if (this.filterForm.endLastLoginDate) {
      const transformedDate = new Date(this.filterForm.endLastLoginDate);
      if (!isNaN(transformedDate.getTime())) {
        this.filterForm.endLastLoginDate = this.datePipe.transform(
          transformedDate,
          'dd-MM-yyyy'
        );
      }
    }

    this.getUserList(1, this.pageSize, this.filterForm);
  }

  getUserList(page: number, size: number, filters?: any): void {
    this.userListLoading = true;
    this.userService
      .getUserList(page, size, filters)
      .pipe(
        finalize(() => {
          this.userListLoading = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.users = res.items;
          this.totalUser = res.count;
          this.totalPage = res.pages;
          this.pageSize = res.size;
          this.currentPage = page;
        },
        error: () => {
          this.toastMessage = 'Error occured while getting the user list.';
          this.toastType = 'error';
        },
      });
  }

  clearFilters(): void {
    this.filterForm.nameSurname = '';
    this.filterForm.role = null;
    this.filterForm.email = '';
    this.filterForm.pid = '';
    this.filterForm.status = null;
    this.filterForm.startCreatedDate = null;
    this.filterForm.endCreatedDate = null;
    this.filterForm.startLastLoginDate = null;
    this.filterForm.organization = null;
    this.getUserList(this.currentPage, this.pageSize);
  }

  changePage(page: number): void {
    if (page > 0 && page <= this.totalPage) {
      this.getUserList(page, this.pageSize, this.filterForm);
    }
  }

  changePageSize(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const size = Number(selectElement.value);
    this.pageSize = size;
    this.currentPage = 1;
    this.getUserList(this.currentPage, this.pageSize, this.filterForm);
  }

  getUserStatusClass(status: string): string {
    if (status === 'Active') return 'badge badge-light-success';
    else if (status === 'DeActive') return 'badge badge-light-danger';
    else return 'badge badge-light-secondary';
  }

  getOrganizations(): void {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
      error: () => {},
    });
  }

  deleteUser(id: string): void {
    if (id) {
      this.userService
        .deleteUser(id)
        .pipe(
          finalize(() => {
            this.showToast = true;
          })
        )
        .subscribe({
          next: () => {
            this.toastMessage = 'User has been deleted.';
            this.toastType = 'success';
            this.users = this.users.filter((user) => user.id !== id);
            this.totalUser--;
            if (!this.users.length && this.currentPage !== 1) {
              this.getUserList(--this.currentPage, this.pageSize);
            }
          },
          error: () => {
            this.toastMessage = 'Error occured while deleting the user.';
            this.toastType = 'error';
          },
        });
    }
  }

  blockUser(id: string): void {
    this.userService.blockUser(id).subscribe({
      next: () => {
        this.toastMessage = 'User has been blocked.';
        this.toastType = 'success';
        this.getUserList(1, this.pageSize, this.filterForm);
      },
      error: () => {
        this.toastMessage = 'Error occured while blocking the user.';
        this.toastType = 'error';
      },
    });
  }

  activateUser(id: string): void {
    this.userService.activateUser(id).subscribe({
      next: () => {
        this.toastMessage = 'User has been activated.';
        this.toastType = 'success';
        this.getUserList(1, this.pageSize, this.filterForm);
      },
      error: () => {
        this.toastMessage = 'Error occured while activating the user.';
        this.toastType = 'error';
      },
    });
  }
}
